import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { Search, Users, Building2, User, Globe, ChevronLeft, ChevronRight } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

export default function MembersDirectory() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [memberType, setMemberType] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const timer = setTimeout(fetchMembers, search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [user, authLoading, search, memberType, page]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (search) params.append('search', search);
      if (memberType) params.append('member_type', memberType);
      const { data } = await axios.get(
        `${API_BASE_URL}/api/membership-applications/directory?${params.toString()}`,
        { headers: authHeaders(), withCredentials: true }
      );
      setMembers(data.members);
      setPages(data.pages);
      setTotal(data.total);
    } catch (error) {
      console.error('Error fetching directory:', error);
    } finally {
      setLoading(false);
    }
  };

  const initials = (name) => (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const MemberCard = ({ m }) => {
    const img = m.member_type === 'institutional' ? m.logo_url : m.photo_url;
    return (
      <Card className="p-6 hover:shadow-xl transition-all duration-300" data-testid={`member-card-${m.id}`}>
        <div className="flex items-start gap-4">
          {img ? (
            <img src={img} alt={m.name} className="h-16 w-16 rounded-full object-cover border-2 border-[#E87722]/30 flex-shrink-0" />
          ) : (
            <div className="h-16 w-16 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
              {initials(m.member_type === 'institutional' ? m.organization_name : m.name)}
            </div>
          )}
          <div className="min-w-0">
            <h3 className="font-bold text-[#1E3A5F] truncate">
              {m.member_type === 'institutional' ? (m.organization_name || m.name) : m.name}
            </h3>
            <p className="text-sm text-gray-600 truncate">
              {m.member_type === 'institutional'
                ? (m.organization_type || 'Institution')
                : [m.designation, m.organization_affiliation].filter(Boolean).join(' • ') || 'Professional'}
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge className={m.member_type === 'institutional' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                {m.member_type === 'institutional' ? <Building2 className="h-3 w-3 mr-1" /> : <User className="h-3 w-3 mr-1" />}
                {m.member_type === 'institutional' ? 'Institutional' : 'Individual'}
              </Badge>
              {m.selected_plan && (
                <Badge variant="outline" className="text-xs">{m.selected_plan}</Badge>
              )}
            </div>
            {m.member_type === 'institutional' && m.nature_of_business && !m.bio && (
              <p className="text-xs text-gray-500 mt-2 line-clamp-2">{m.nature_of_business}</p>
            )}
            {m.bio && (
              <p className="text-xs text-gray-500 mt-2 line-clamp-2">{m.bio}</p>
            )}
            {m.website_url && (
              <a
                href={m.website_url.startsWith('http') ? m.website_url : `https://${m.website_url}`}
                target="_blank" rel="noreferrer"
                className="text-xs text-[#E87722] mt-2 inline-flex items-center gap-1 hover:underline"
              >
                <Globe className="h-3 w-3" />Website
              </a>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5F8D] pt-32 pb-10">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3" data-testid="members-directory-title">
            <Users className="h-8 w-8" /> Member Directory
          </h1>
          <p className="text-blue-100 mt-2">Discover the changemakers in the CSED network</p>
        </div>
      </div>

      <main className="flex-grow">
        <div className="container mx-auto px-4 py-10">
          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search by name, organisation or expertise..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-10 bg-white"
                data-testid="directory-search-input"
              />
            </div>
            <div className="bg-white rounded-lg p-1 shadow-sm inline-flex">
              {[['', 'All'], ['individual', 'Individual'], ['institutional', 'Institutional']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => { setMemberType(val); setPage(1); }}
                  className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                    memberType === val ? 'bg-[#E87722] text-white' : 'text-gray-600 hover:text-[#E87722]'
                  }`}
                  data-testid={`directory-filter-${val || 'all'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
            </div>
          ) : members.length === 0 ? (
            <Card className="p-12 text-center" data-testid="directory-empty-state">
              <Users className="h-14 w-14 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">No members found</h3>
              <p className="text-gray-600">
                {search || memberType
                  ? 'Try adjusting your search or filters.'
                  : 'Verified members will appear here once their applications are approved.'}
              </p>
            </Card>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4" data-testid="directory-result-count">{total} member{total !== 1 ? 's' : ''} found</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {members.map((m) => <MemberCard key={m.id} m={m} />)}
              </div>

              {pages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-10">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="directory-prev-page">
                    <ChevronLeft className="h-4 w-4 mr-1" />Previous
                  </Button>
                  <span className="text-sm text-gray-600">Page {page} of {pages}</span>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="directory-next-page">
                    Next<ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
